'use client';

import { useSearchParams } from 'next/navigation';
import { useGetLastViewedPathQuery } from '@frameio/graphql-client/graphql/features/integrations';
import { isServer } from '@frameio/utils';
import { isValidIntegrationsRedirectPath } from '@frameio/utils/validators/isValidIntegrationsRedirectPath';

/**
 * This hook is used to determine where to redirect a after determining their
 * authentication status. In order or precedence, we redirect them to:
 *   * a redirectPath query param
 *   * the last viewed path persisted in UXP
 *   * the defaultPath sent as an argument
 *
 * The hook validates the redirect path to ensure it is a safe, same-origin path.
 *
 * @param defaultPath - The default path to redirect to if the last viewed path is not set.
 * @returns The redirect path.
 */
export const useRedirectPath = (defaultPath: string) => {
  const lastViewedPath = useGetLastViewedPathQuery({
    context: {
      resolveWithUXP: true,
    },
    skip: isServer,
  })?.data?.getLastViewedPath?.path;

  const lastViewedPathToUse =
    lastViewedPath &&
    !lastViewedPath.includes('/view/') &&
    isValidIntegrationsRedirectPath(lastViewedPath)
      ? lastViewedPath
      : false;

  const searchParams = useSearchParams();
  const redirectQueryPath = searchParams.get('redirectPath');

  const redirectPath =
    redirectQueryPath && isValidIntegrationsRedirectPath(redirectQueryPath)
      ? redirectQueryPath
      : lastViewedPathToUse;

  return redirectPath || defaultPath;
};
